import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { deleteR2Folder, renameR2Folder } from 'src/actions/watcher/r2FilesActions';
import { R2FolderInterface } from 'src/store/watcher/r2FilesSlice';
import { List, Modal, Input, message } from 'antd';
import { FolderOutlined, DeleteOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';

interface Props {
  onNavigate: (prefix: string) => void;
}

const FolderList = ({ onNavigate }: Props) => {
  const dispatch = useAppDispatch();
  const folders = useAppSelector(state => state.watcher.r2Files.folders);
  const activeBucketId = useAppSelector(state => state.watcher.r2Files.activeBucketId);
  const [editingPrefix, setEditingPrefix] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deletingFolder, setDeletingFolder] = useState<R2FolderInterface | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');

  async function handleDeleteFolder() {
    if (!deletingFolder) return;
    const resultAction = await dispatch(deleteR2Folder({ prefix: deletingFolder.prefix, bucket_id: activeBucketId || undefined }));
    if (deleteR2Folder.fulfilled.match(resultAction)) {
      message.success(`Folder "${deletingFolder.name}" deleted`);
    } else {
      message.error('Failed to delete folder');
    }
    setDeletingFolder(null);
    setDeleteConfirmName('');
  }

  function startRename(folder: R2FolderInterface) {
    setEditingPrefix(folder.prefix);
    setEditingName(folder.name);
  }

  function cancelRename() {
    setEditingPrefix(null);
    setEditingName('');
  }

  async function confirmRename(oldPrefix: string) {
    const trimmed = editingName.trim();
    if (!trimmed) {
      message.warning('Folder name cannot be empty');
      return;
    }

    const resultAction = await dispatch(renameR2Folder({ prefix: oldPrefix, folder_name: trimmed, bucket_id: activeBucketId || undefined }));
    if (renameR2Folder.fulfilled.match(resultAction)) {
      message.success('Folder renamed');
      setEditingPrefix(null);
      setEditingName('');
    } else {
      message.error('Failed to rename folder');
    }
  }

  if (!folders.length) return null;

  return (
    <>
    <List
      size="small"
      dataSource={folders}
      style={{ marginBottom: 16 }}
      renderItem={(folder) => {
        const isEditing = editingPrefix === folder.prefix;

        return (
          <List.Item
            actions={
              isEditing
                ? [
                    <CheckOutlined
                      key="confirm"
                      style={{ color: '#52c41a', cursor: 'pointer' }}
                      onClick={() => confirmRename(folder.prefix)}
                    />,
                    <CloseOutlined
                      key="cancel"
                      style={{ cursor: 'pointer' }}
                      onClick={cancelRename}
                    />
                  ]
                : [
                    <EditOutlined
                      key="rename"
                      style={{ cursor: 'pointer' }}
                      onClick={() => startRename(folder)}
                    />,
                    <DeleteOutlined
                      key="delete"
                      style={{ color: '#ff4d4f', cursor: 'pointer' }}
                      onClick={() => setDeletingFolder(folder)}
                    />
                  ]
            }
          >
            <List.Item.Meta
              avatar={<FolderOutlined style={{ fontSize: 20, color: '#faad14' }} />}
              title={
                isEditing
                  ? <Input
                      size="small"
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      onPressEnter={() => confirmRename(folder.prefix)}
                      onKeyDown={e => e.key === 'Escape' && cancelRename()}
                      autoFocus
                      style={{ width: 250 }}
                    />
                  : <span onClick={() => onNavigate(folder.prefix)} style={{ cursor: 'pointer' }}>
                      {folder.name}
                    </span>
              }
            />
          </List.Item>
        );
      }}
    />

    <Modal
      title="Delete folder"
      open={!!deletingFolder}
      onOk={handleDeleteFolder}
      onCancel={() => { setDeletingFolder(null); setDeleteConfirmName(''); }}
      okText="Delete"
      okButtonProps={{ danger: true, disabled: deleteConfirmName !== deletingFolder?.name }}
    >
      <p>This will permanently delete the folder and all its contents.</p>
      <p>Type <strong>{deletingFolder?.name}</strong> to confirm:</p>
      <Input
        value={deleteConfirmName}
        onChange={e => setDeleteConfirmName(e.target.value)}
        placeholder={deletingFolder?.name}
        autoFocus
      />
    </Modal>
    </>
  );
};

export default FolderList;
